/**
 * The pieces of the Priced Scenarios list: the status tabs and the "Only my
 * scenarios" switch on the card's top row, the search box and type chips on
 * the toolbar under it, and the Priced-by cell.
 *
 * Colors come from the Lendmarq system (`theme/colors.js`). The status tabs are
 * in the URL (`?status=`), so they take the level-1 (teal) active state; the
 * type chips filter the rows in place and take level 2 (steel).
 */
import { forwardRef } from "react";

import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";

import UserAvatar from "../../../components/ui/UserAvatar";
import Txt from "../../../components/typography/Txt";
import { level1, level2, slate, steel } from "../../../theme/colors";
import { SZ } from "../../sizersShared/sizerUi";

export {
  NUM,
  ScenarioStatusPill,
  TypeTag,
} from "../../sizersShared/workspace/workspaceParts";

const FOCUS_SX = {
  "&:focus-visible": {
    outline: `2px solid ${level1.indicator}`,
    outlineOffset: "2px",
  },
};

const CountBadge = ({ on, children }) => (
  <Box
    component="span"
    sx={{
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: "18px",
      minWidth: 20,
      px: "6px",
      borderRadius: "99px",
      textAlign: "center",
      bgcolor: on ? level1.tint : slate[100],
      color: on ? level1.text : slate[500],
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {children}
  </Box>
);

/* ============================================================ Top row */

/** Active · Converted · Archived, each with its count. */
export const StatusTabs = ({ options, value, onChange }) => (
  <Box
    role="tablist"
    aria-label="Scenario status"
    sx={{ display: "flex", gap: 0.5 }}
  >
    {options.map((o) => {
      const on = value === o.value;
      return (
        <Box
          key={o.value}
          component="button"
          type="button"
          role="tab"
          aria-selected={on}
          onClick={() => onChange(o.value)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.875,
            border: 0,
            background: "none",
            font: "inherit",
            fontSize: 14,
            fontWeight: on ? 600 : 500,
            color: on ? level1.text : slate[500],
            padding: "14px 10px 12px",
            mb: "-1px",
            borderBottom: `2px solid ${on ? level1.text : "transparent"}`,
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": { color: on ? level1.text : slate[700] },
            ...FOCUS_SX,
          }}
        >
          {o.label}
          <CountBadge on={on}>{o.count}</CountBadge>
        </Box>
      );
    })}
  </Box>
);

/** The "Only my scenarios" switch — a yes/no filter, so a switch. */
export const OnlyMineSwitch = ({ checked, onChange }) => (
  <Box
    component="label"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      cursor: "pointer",
      fontSize: 13.5,
      color: SZ.ink700,
      userSelect: "none",
    }}
  >
    <Switch
      size="small"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      inputProps={{ "aria-label": "Only my scenarios" }}
    />
    Only my scenarios
  </Box>
);

/* ============================================================ Toolbar */

/**
 * The search box that leads the toolbar. `/` focuses it from anywhere on the
 * page (the hint sits at the right edge), × clears it, Esc clears it too.
 */
export const SearchBox = forwardRef(({ value, onChange, placeholder }, ref) => (
  <Box
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      flex: "1 1 260px",
      maxWidth: 380,
      minWidth: 200,
      height: 38,
      px: 1.25,
      gap: 1,
      bgcolor: "#fff",
      border: `1px solid ${SZ.lineStrong}`,
      borderRadius: SZ.radiusSm,
      transition: "border-color .12s, box-shadow .12s",
      "&:focus-within": {
        borderColor: SZ.teal500,
        boxShadow: `0 0 0 3px ${SZ.teal50}`,
      },
    }}
  >
    <SearchRounded sx={{ fontSize: 18, color: slate[400] }} />
    <Box
      component="input"
      ref={ref}
      value={value}
      placeholder={placeholder}
      aria-label="Search scenarios"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onChange("");
          e.currentTarget.blur();
        }
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        border: 0,
        outline: "none",
        bgcolor: "transparent",
        font: "inherit",
        fontSize: 13.5,
        color: SZ.ink900,
        "&::placeholder": { color: slate[400] },
      }}
    />
    {value ? (
      <Box
        component="button"
        type="button"
        aria-label="Clear search"
        onClick={() => onChange("")}
        sx={{
          display: "inline-flex",
          border: 0,
          background: "none",
          p: 0,
          cursor: "pointer",
          color: slate[400],
          "&:hover": { color: slate[600] },
        }}
      >
        <CloseRounded sx={{ fontSize: 17 }} />
      </Box>
    ) : (
      <Box
        component="kbd"
        sx={{
          fontFamily: SZ.mono,
          fontSize: 11,
          color: slate[400],
          border: `1px solid ${SZ.line}`,
          borderRadius: "4px",
          px: "5px",
          lineHeight: "16px",
        }}
      >
        /
      </Box>
    )}
  </Box>
));
SearchBox.displayName = "SearchBox";

/** A type filter chip with its count. */
export const FilterChip = ({ on, label, count, onClick }) => (
  <Box
    component="button"
    type="button"
    aria-pressed={on}
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      height: 34,
      px: 1.5,
      borderRadius: "99px",
      border: `1px solid ${on ? level2.border : SZ.lineStrong}`,
      bgcolor: on ? level2.tint : "#fff",
      color: on ? level2.text : SZ.ink700,
      font: "inherit",
      fontSize: 13,
      fontWeight: on ? 600 : 500,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "background .12s, border-color .12s",
      "&:hover": { borderColor: level2.border },
      ...FOCUS_SX,
    }}
  >
    {label}
    <Box
      component="span"
      sx={{
        fontWeight: 500,
        color: on ? level2.text : slate[400],
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {count}
    </Box>
  </Box>
);

/* ============================================================ Cells */

/** Avatar initials + name. */
export const PersonCell = ({ name, email }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
    <UserAvatar
      size={26}
      label={name || email || ""}
      tooltip={email}
      showTooltip={Boolean(email)}
      sx={{
        bgcolor: steel.tint,
        color: steel.main,
        fontWeight: 600,
        fontSize: 10.5,
      }}
    />
    <Txt
      component="div"
      sx={{
        fontSize: 13,
        color: SZ.ink900,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {name || email || "—"}
    </Txt>
  </Box>
);
